import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Alert,
  Button,
  DialogContent,
  Divider,
  IconButton,
} from "@mui/material";
import { ChangeEvent, ReactElement, useContext, useRef } from "react";

import useText from "../../hooks/use-text";
import {
  UploadContext,
  UploadFormErrorTypes,
} from "../../providers/upload-provider";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";

import AssetCategorySelector from "./selector/asset-category-selector";
import AssetPrefixSelector from "./selector/asset-prefix-selector";
import AssetTypeSelector from "./selector/asset-type-selector";
import CollectionSelector from "./selector/collection-selector";
import LabelTypeSelector from "./selector/label-type-selector";
import LanguageRegionSelector from "./selector/language-region-selector";
import MetashotsTypeSelector from "./selector/metashots-type-selector";
import ProductTypeSelector from "./selector/product-type-selector";
import StatusSelector from "./selector/status-selector";
import TypeOfShotSelector from "./selector/type-of-shot-selector";

export default function UploadModalContent(): ReactElement {
  const { strings } = useText(local);

  const {
    files,
    setFiles,
    errors,
    setAssetTypes,
    setAssetStatus,
    setMetashotType,
    setLanguageRegion,
    setCollection,
    setCategory,
    setProjectId,
    setAdaptionId,
    setMasterId,
    setProductTypeId,
    setLabelTypeId,
    setTypeOfShotId,
  } = useContext(UploadContext);

  const inputRef = useRef<HTMLDivElement>(null);

  function onFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const newFiles: File[] = [];

    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    Array.from(e.target.files).forEach((file) => newFiles.push(file));

    setFiles([...files, ...newFiles]);

    inputRef.current?.classList.remove("!bg-cyan-50");
  }

  function removeFile(index: number): void {
    setFiles(files.filter((_, idx) => idx !== index));
  }

  return (
    <DialogContent>
      {errors.length > 0 && (
        <Alert severity="error">{strings.error.general}</Alert>
      )}
      <div className="w-full flex items-center flex-row text-center relative gap-[10px] flex-wrap max-h-[375px] overflow-auto">
        {files.map((file, index) => (
          <div key={file.name + index} className="relative z-10">
            <img
              className="max-w-[100px] mb-[15px]"
              key={index}
              src={URL.createObjectURL(file)}
              alt={`${file.name} preview`}
            />
            <IconButton
              className="absolute right-[-5px] top-[-5px]"
              aria-label="delete"
              size="small"
              onClick={() => removeFile(index)}
            >
              <HighlightOffIcon fontSize="inherit" />
            </IconButton>
          </div>
        ))}
      </div>
      <div
        ref={inputRef}
        style={{
          background: "#F6F6F6",
          height: files.length ? "150px" : "350px",
        }}
        className="w-full rounded-[10px] flex items-center flex-col text-center relative"
      >
        <CloudUploadOutlinedIcon
          style={{
            fontSize: files.length ? "40px" : "170px",
            marginTop: files.length ? "20px" : "30px",
          }}
          className="text-slate-300"
        />
        Drag and Drop assets here or
        <Button className="text-[14px] mt-10 px-10 capitalize text-slate-600 border-slate-600 rounded-2xl border-solid border-[1px]">
          Browse
        </Button>
        <input
          onDragEnter={() => {
            inputRef.current?.classList.add("!bg-cyan-50");
          }}
          onDragLeave={() => {
            inputRef.current?.classList.remove("!bg-cyan-50");
          }}
          onChange={onFileChange}
          multiple
          accept="image/*, application/pdf, video/*, application/photoshop"
          className="z-0 absolute top-0 left-0 w-full bottom-0 opacity-0 cursor-pointer"
          type="file"
        />
      </div>
      <div className="mt-[15px]">
        <div>{files.length} files</div>
        <Divider className="my-10" />
        <span className="text-[14px] mr-[10px]">
          Please enter Asset details below
        </span>
        <span className="text-[10px]">*Mandatory</span>
        <div className="mt-[15px] grid grid-cols-3 gap-10">
          <AssetTypeSelector
            error={errors.includes(UploadFormErrorTypes.MISSING_ASSET_TYPE)}
            onChangeAssetTypes={setAssetTypes}
          />
          <ProductTypeSelector onChangeProductTypes={setProductTypeId} />
          <LabelTypeSelector onChangeLabelTypes={setLabelTypeId} />
          <TypeOfShotSelector onChangeTypeOfShots={setTypeOfShotId} />
          <StatusSelector
            error={errors.includes(UploadFormErrorTypes.MISSING_STATUS_TYPE)}
            onChangeStatus={setAssetStatus}
          />
          <MetashotsTypeSelector onChange={setMetashotType} />
          <LanguageRegionSelector onChange={setLanguageRegion} />
          <CollectionSelector onChange={setCollection} />
          <AssetCategorySelector onChange={setCategory} />
          <AssetPrefixSelector
            maxLength={5}
            placeholder="Project ID"
            prefix="P-"
            onChange={setProjectId}
          />
          <AssetPrefixSelector
            maxLength={7}
            placeholder="Master ID"
            prefix="MAS-"
            onChange={setMasterId}
          />
          <AssetPrefixSelector
            maxLength={9}
            placeholder="Adaption ID"
            prefix="AD-ID-"
            onChange={setAdaptionId}
          />
        </div>
      </div>
    </DialogContent>
  );
}

type LocalSchema = {
  error: {
    general: string;
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    error: {
      general: "Please fill out the mandatory fields before submitting.",
    },
  },
};
